import React, { ReactNode } from "react";
import ProfileContainer from "./ProfileContainer";
import {
  Button,
  CircularProgress,
  Grid,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import useInvestorAmlHistoryStore from "../zstore/investorAmlHistory";
import { useShallow } from "zustand/react/shallow";
import { formatDate } from "../utils/date";
import MarkerDisplay from "./MarkerDisplay";
import useInvestorMarkerStore from "../zstore/investorMarkers";
import { find } from "lodash";
import { IDVGroupMarkers, MarkerGroups } from "../constant/markerConstant";
import MarkerUpdateButton from "./MarkerUpdateButton";
import MarkerDialog, { useMarkerDialog } from "./MarkerDialog";
import useInvestorAmlDetailStore from "../zstore/investorAmlDetail";
import { useSelector } from "react-redux";
import { selectors } from "../amlSlice";
import useHighlightStore, { ItemType } from "../zstore/highlightStore";
import restUtils from "core/utils/rest";
import useMessage from "../hooks/useMessage";
import useInvestorEvidenceRequestStore from "../zstore/investorEvidenceRequest";
import {
  EvidenceRequestsRelateToIDVS,
  EvidenceRequestStatusEnum,
  NeglectableEvidenceRequestStatus,
} from "../constant/requiredEvidence";
import useCheckResult from "../hooks/useCheckResult";

type IdVerificationProfileProps = {
  personId: string;
};

const IdVerificationProfile: React.FC<IdVerificationProfileProps> = ({
  personId,
}) => {
  const { latestAmlHistory, amlHistoryLoading } = useInvestorAmlHistoryStore(
    useShallow((state) => ({
      latestAmlHistory: state.getLatestHistory(),
      amlHistoryLoading: state.loading,
    }))
  );
  const { amlDetail, amlDetailLoading } = useInvestorAmlDetailStore(
    useShallow((state) => ({
      amlDetail: state.data,
      amlDetailLoading: state.loading,
    }))
  );
  const { requiredEvidences, requiredEvidenceLoading } =
    useInvestorEvidenceRequestStore(
      useShallow((state) => ({
        requiredEvidences: state.data,
        requiredEvidenceLoading: state.loading,
      }))
    );
  const message = useMessage();
  const removeHighlightedItem = useHighlightStore((state) => state.removeItem);
  const markers = useSelector(selectors.markers);
  const {
    data: investorMarkers,
    loading: investorMarkerLoading,
    fetchData: refetchInvestorMarkers,
  } = useInvestorMarkerStore();
  const {
    open: markerDialogOpen,
    closeDialog: closeMarkerDialog,
    formConfig: markerDialogFormConfig,
    initialValue: markerDialogInitial,
    openGroupMarkerAddDialog,
    openMarkerUpdateDialog,
  } = useMarkerDialog();
  const handleCheckResult = useCheckResult();
  const idvMarker = find(investorMarkers, {
    groupCode: MarkerGroups.IDVS,
    removed: false,
  });
  const getLinkOfFile = async (urn: string) => {
    const queryParams = {
      jd: '{ "cmd": "getdll", "fu": "' + urn + '", "fv": 1 }',
    } as const;
    const queryString = Object.keys(queryParams)
      .map(
        (key) =>
          `${encodeURIComponent(key)}=${encodeURIComponent(
            queryParams[key as keyof typeof queryParams]
          )}`
      )
      .join("&");
    const reportFileLocation = `DocumentServlet?${queryString}`;
    await restUtils
      .call("PortalApi", `${reportFileLocation}`, "POST")
      .then((res: any) => {
        if (res.data.status === "SUCCESS" && res.data.statusMessageExtra) {
          window.open(res.data.statusMessageExtra, "_blank");
        } else {
          message.error("Unable to open a new report window");
        }
      })
      .catch((err) => {
        console.error(err);
      });
  };
  const handleUpdateMarker = () => {
    if (idvMarker) {
      openMarkerUpdateDialog(idvMarker, amlDetail!);
    } else {
      openGroupMarkerAddDialog(MarkerGroups.IDVS, amlDetail!);
    }
  };
  const handleRequiredEvidenceCheck = (targetMarkerCode: string) => {
    const isPassUpdating =
      idvMarker?.code !== IDVGroupMarkers.PASS &&
      targetMarkerCode === IDVGroupMarkers.PASS;
    const invalidRequiredEvidences = requiredEvidences.filter((el) => {
      // IDVS related required evidence exist and is not in final status.
      return (
        EvidenceRequestsRelateToIDVS.includes(el.code) &&
        !NeglectableEvidenceRequestStatus.includes(
          el.status as EvidenceRequestStatusEnum
        )
      );
    });
    if (isPassUpdating && invalidRequiredEvidences.length > 0) {
      closeMarkerDialog();
      handleCheckResult({
        results: invalidRequiredEvidences,
        idProps: "code",
        msg: "The ID&V Status cannot be changed to Passed until the Identification and Address Evidence is verified.",
        scroll: true,
        type: ItemType.EVIDENCE_REQUEST,
      });
      return false;
    }
    return true;
  };
  return (
    <ProfileContainer id="id-and-v-section" title="ID&V">
      <Grid container>
        <Grid item sm={12} md={5.5} xs={12}>
          <Stack direction="column" rowGap="10px">
            <Item title="ID&V Status: ">
              <Stack alignItems="center" direction="row" columnGap="24px">
                <MarkerDisplay
                  marker={idvMarker}
                  loading={investorMarkerLoading}
                />
                <MarkerDialog
                  open={markerDialogOpen}
                  formConfig={markerDialogFormConfig!}
                  initialValue={markerDialogInitial!}
                  markers={markers}
                  onClose={closeMarkerDialog}
                  onSuccess={(data) => {
                    closeMarkerDialog();
                    refetchInvestorMarkers(personId, markers);
                    removeHighlightedItem({
                      id: data.previous!,
                      type: ItemType.MARKER,
                    });
                  }}
                  onPreconditionCheck={handleRequiredEvidenceCheck}
                />
                <MarkerUpdateButton
                  markerCode={idvMarker?.code}
                  onClick={handleUpdateMarker}
                  disabled={
                    amlDetailLoading ||
                    investorMarkerLoading ||
                    requiredEvidenceLoading
                  }
                />
              </Stack>
            </Item>
            <Item title="Date of Search: ">
              <Typography>
                {amlHistoryLoading ? (
                  <CircularProgress size="1rem" />
                ) : latestAmlHistory ? (
                  formatDate(latestAmlHistory.resultDate)
                ) : (
                  "-"
                )}
              </Typography>
            </Item>
          </Stack>
        </Grid>
        <Grid item sm={12} md={4.5} xs={12}>
          <Stack direction="column" rowGap="10px">
            {latestAmlHistory?.resultPdfExt_dvUrn && (
              <Link
                onClick={() =>
                  getLinkOfFile(latestAmlHistory?.resultPdfExt_dvUrn)
                }
              >
                Smartsearch Report
              </Link>
            )}
            {latestAmlHistory?.resultPdfInt_dvUrn && (
              <Link
                onClick={() =>
                  getLinkOfFile(latestAmlHistory?.resultPdfInt_dvUrn)
                }
              >
                Hubwise AML Report
              </Link>
            )}
            {latestAmlHistory?.supplierLink && (
              <Link href={latestAmlHistory?.supplierLink}>
                View in Smartsearch
              </Link>
            )}
          </Stack>
        </Grid>
      </Grid>
    </ProfileContainer>
  );
};

const Item: React.FC<{ title: string; children?: ReactNode }> = ({
  title,
  children,
}) => {
  return (
    <Grid alignItems="center" container>
      <Grid item xs={3.5}>
        <Typography sx={{ fontStyle: "italic" }}>{title}</Typography>
      </Grid>
      <Grid item xs={6} sx={{ ml: "12.5%" }}>
        {children}
      </Grid>
    </Grid>
  );
};
export default IdVerificationProfile;
